import { AnyAction } from 'redux';
import { opApiRequest } from 'src/api/apiRequest';
import endpoints from 'src/api/opEndpoints';
import getAbstractProduct from 'src/store/design/selector/getAbstractProduct';
import getCategoryId from 'src/store/design/selector/getCategoryId';
import getDesignId from 'src/store/design/selector/getDesignId';
import getEncPublicDesignId from 'src/store/design/selector/getEncPublicDesignId';
import getProductId from 'src/store/design/selector/getProductId';
import getStructure from 'src/store/design/selector/getStructure';
import isPublicDesign from 'src/store/design/selector/isPublicDesign';
import { Store } from 'src/store/index';
import { ThunkDispatch } from 'redux-thunk';
import ensureCorrectProductUid from 'src/util/design/ensureCorrectProductUid';
import findMugWrapAroundProduct from 'src/util/design/findMugWrapAroundProduct';
import getDesignContentHash from 'src/util/design/getDesignContentHash';
import useIntegrationLayer from 'src/util/hook/useIntegrationLayer';

const updateCustomerDesignOperation = () => async (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
  getState: () => Store,
) => {
  const store = getState();

  if (isPublicDesign(store)) {
    return false;
  }

  const {
    clientPlatform,
  } = useIntegrationLayer();

  const structure = getStructure(store);
  const designId = getDesignId(store);
  const categoryId = getCategoryId(store);
  const encPublicDesignId = getEncPublicDesignId(store);
  const productId = getProductId(store);
  const abstractProduct = getAbstractProduct(store);

  if (!structure || !designId || !categoryId) {
    return false;
  }

  // Ensure the correct product_uid is set based on populated spreads
  const structureToSave = ensureCorrectProductUid(structure);

  const contentHash = getDesignContentHash(structureToSave);

  // Mug print-area variants (one-side / wrap-around / ...) share the same productUid
  // and differ only by internalUid + the OpMugPrintArea attribute, so resolving the
  // bound product by productUid alone lands on the first (one-side) variant and the
  // design is produced/priced one-side. Mugs must be produced wrap-around, so prefer
  // the wrap-around sibling when one exists; otherwise fall back to the productUid
  // match (unchanged behaviour for products without a wrap sibling).
  const wrapAroundProduct = findMugWrapAroundProduct(abstractProduct, structureToSave.product_uid);
  const matchedProductId = abstractProduct?.products?.find((p) => p.productUid === structureToSave.product_uid)?.productId;

  const requestData = {
    customerDesignId: designId,
    categoryId,
    structure: structureToSave,
    clientPlatform,
    isDesignCompleted: true,
    contentHash,
    encPublicDesignId,
    productId: abstractProduct
      ? (wrapAroundProduct?.productId ?? matchedProductId ?? productId)
      : null,
  };

  try {
    await opApiRequest(
      endpoints.CUSTOMER_DESIGN_V3_UPDATE,
      requestData,
      'data',
      'POST',
    );
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Cannot update customer design', e);
    return false;
  }
};

export default updateCustomerDesignOperation;
